import { Auth0ContextInterface } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef } from "react";
import RemergeApi from "../http/RemergeApi";
import { buildRedirectState, stashReturnTo } from "./redirects";
import { AppUser } from "./types";
import { useAuth0Provision } from "./useAuth0Provision";

export type UseSharedAuthLifecycleParams = {
  remergeApi: RemergeApi;
  auth0: Pick<
    Auth0ContextInterface,
    "isLoading" | "isAuthenticated" | "loginWithRedirect" | "logout" | "user" | "error"
  >;
  onAuthenticated?: (user: AppUser) => void;
  onUnauthenticated?: () => void;
  onProvisionError?: () => void;
};

export type UseSharedAuthLifecycleResult = {
  user: AppUser | undefined;
  isResolvingAuth: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
};

/**
 * Shared Auth0 lifecycle orchestration for non-admin apps.
 *
 * Unauthenticated users are sent directly to Auth0 universal login via
 * loginWithRedirect — no intermediate login hub.
 *
 * Provisioning failure (the /auth0/provision endpoint rejects or errors) signs
 * the user out of Auth0 and lets the next mount restart the flow. Without this,
 * the user is stuck: Auth0 reports authenticated but there's no Rails User to
 * back the session, the redirect effect exits early on auth0.isAuthenticated,
 * and consumers render `null` forever.
 */
export function useSharedAuthLifecycle({
  remergeApi,
  auth0,
  onAuthenticated,
  onUnauthenticated,
  onProvisionError,
}: UseSharedAuthLifecycleParams): UseSharedAuthLifecycleResult {
  // Tracks whether we initiated an auth0.logout from the provisioning-error
  // path. The SDK clears its state synchronously before the /v2/logout
  // navigation completes, which causes redirectUnauthenticatedUsers to see
  // auth0.isAuthenticated=false and fire loginWithRedirect — racing the
  // logout navigation. The ref suppresses that second navigation.
  const isLoggingOutRef = useRef(false);

  const handleProvisionError = useCallback(
    () => {
      onProvisionError?.();
      onUnauthenticated?.();

      if (auth0.isAuthenticated) {
        isLoggingOutRef.current = true;

        // The logout round trip returns to the bare origin; stash the deep
        // link so the follow-up login can restore it.
        stashReturnTo(window.location.pathname, window.location.search);
        auth0.logout({ logoutParams: { returnTo: window.location.origin } });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth0 object is stable, individual properties are tracked
    [auth0.isAuthenticated, auth0.logout, onProvisionError, onUnauthenticated],
  );

  const { isProvisioning, provisionedUser, clearProvisionedUser, resetProvisionAttempt } = useAuth0Provision({
    isLoading: auth0.isLoading,
    isAuth0Authenticated: auth0.isAuthenticated,
    remergeApi,
    auth0User: auth0.user,
    onProvisionError: handleProvisionError,
  });

  const user: AppUser | undefined = provisionedUser;
  const isResolvingAuth = (auth0.isLoading && !auth0.error) || isProvisioning;
  const isAuthenticated = Boolean(user?.id) || auth0.isAuthenticated;

  const logout = useCallback(
    async () => {
      onUnauthenticated?.();

      if (auth0.isAuthenticated) {
        auth0.logout({ logoutParams: { returnTo: window.location.origin } });
      } else {
        window.location.reload();
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth0 object is stable, individual properties are tracked
    [auth0.isAuthenticated, auth0.logout, onUnauthenticated],
  );

  useEffect(syncAuth0ProvisionState, [auth0.isAuthenticated, user?.id, resetProvisionAttempt, clearProvisionedUser]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- auth0 object is stable, individual properties are tracked
  useEffect(redirectUnauthenticatedUsers, [isResolvingAuth, user?.id, auth0.isAuthenticated, auth0.loginWithRedirect]);
  useEffect(notifyAuthenticated, [user?.id, onAuthenticated, user]);

  return {
    user,
    isResolvingAuth,
    isAuthenticated,
    logout,
  };

  function syncAuth0ProvisionState() {
    // When Auth0 signs out, always tear down the provisioned user — even if
    // user.id is still briefly populated from the prior render — so a fresh
    // sign-in re-provisions cleanly and a stale provisionedUser can never
    // outlive the Auth0 session.
    if (!auth0.isAuthenticated) {
      clearProvisionedUser?.();
      resetProvisionAttempt?.();
    }

    // Once Auth0 is authenticated and the user is provisioned, leave
    // hasAttemptedProvision true: provisioning is one-shot per Auth0 session
    // and resetting it here would let useAuth0Provision spam /auth0/provision
    // on any re-render that briefly clears provisionedUser.
  }

  function redirectUnauthenticatedUsers() {
    if (isResolvingAuth || user?.id || auth0.isAuthenticated) return;

    // Mid-logout (provisioning failed and we called auth0.logout above):
    // let the /v2/logout navigation complete instead of racing it with a
    // loginWithRedirect.
    if (isLoggingOutRef.current) return;

    // Catch rejections so they don't bubble as unhandled promise rejections
    // when Auth0 is misconfigured or the network is down. The catch callback
    // is always a real function (forwarding to the optional onProvisionError
    // when present) — passing onProvisionError directly would be a no-op when
    // it's undefined, letting the rejection escape. Promise.resolve(...) wrap
    // covers test mocks that return undefined instead of a Promise.
    Promise.resolve(
      auth0.loginWithRedirect({
        appState: buildRedirectState(window.location.pathname, window.location.search),
      }),
    ).catch(() => onProvisionError?.());
  }

  function notifyAuthenticated() {
    if (!user?.id) return;
    onAuthenticated?.(user);
  }
}
